import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { PageMain } from "components/molecules";
import { routes } from "consts";

interface BoundaryProps {
  children: ReactNode;
  /** The boundary forgets a caught error when this changes (another template, another route). */
  resetKey: string;
}

interface BoundaryState {
  error: Error | null;
}

class Boundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidUpdate(previous: BoundaryProps) {
    if (this.state.error && previous.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Edytor szablonu 2.0 przestał działać:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <PageMain>
        <div role="alert" className="max-w-2xl rounded border border-rose-300 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-300">
          <p className="text-base font-semibold">Nie udało się uruchomić edytora szablonu.</p>
          <p className="mt-2">
            Odśwież stronę. Jeśli problem się powtarza, wróć do listy szablonów i spróbuj ponownie później albo
            skontaktuj się z administratorem. Reszta panelu działa normalnie.
          </p>
          {error.message && <p className="mt-2 break-words text-xs text-rose-700">Szczegóły: {error.message}</p>}
          <div className="mt-4 flex items-center gap-4">
            <button
              type="button"
              className="btn bg-primary-500 text-white hover:bg-primary-600"
              onClick={() => window.location.reload()}
            >
              Odśwież stronę
            </button>
            <Link to={routes.DOCUMENTS_V2} className="text-primary-600 hover:underline">
              Wróć do listy
            </Link>
          </div>
        </div>
      </PageMain>
    );
  }
}

/**
 * Keeps a failure of the lazily loaded template editor (the chunk cannot be fetched, the Designer cannot start, a bug)
 * inside the editor's own route instead of blanking the whole admin panel. Not lazy itself: it must be loaded before
 * the chunk it guards.
 */
export const EditorErrorBoundary = ({ children }: { children: ReactNode }): JSX.Element => {
  const { pathname } = useLocation();
  return <Boundary resetKey={pathname}>{children}</Boundary>;
};
